import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Download, ShoppingBag } from "lucide-react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND, THEMES } from "@/lib/releases";
import { PreviewPlayer } from "@/components/release/PreviewPlayer";
import { StreamingLinks } from "@/components/release/StreamingLinks";
import { MusicVideo } from "@/components/release/MusicVideo";
import { BuyModal, RecoverModal, SuccessModal, startDownload } from "@/components/release/ReleaseModals";

const fadeUp = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.7, ease: [0.22, 1, 0.36, 1] } });

function usePurchaseReturn(slug, setToken, setModal) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("status") === "cancel") toast("Checkout cancelled.");
    if (params.get("status") !== "success" || !sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/releases/${slug}/checkout/${sid}`);
        if (r.data.payment_status === "paid") {
          const t = await axios.post(`${BACKEND}/api/releases/${slug}/download-token`, { session_id: sid });
          setToken(t.data.token);
          setModal("success");
          setParams({}, { replace: true });
          return;
        }
      } catch { /* retry below */ }
      if (++tries < 10) setTimeout(poll, 2000);
      else toast.error("We couldn't confirm the payment yet — check your email for the download link.");
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

const ArtistLine = ({ r }) => (
  <div className="text-lg md:text-xl text-white/80" data-testid="release-artist-line">
    <Link to={`/${r.artist_slug}`} className="font-bold text-white underline-offset-4 decoration-white/40 hover:underline" data-testid="release-artist-link">{r.artist}</Link>
    {r.featuring && <span className="text-white/60"> feat. {r.featuring}</span>}
  </div>
);

const Cover = ({ r }) => (
  <motion.div initial={{ opacity: 0, scale: 0.94, rotate: -1.5 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-[520px] mx-auto lg:mx-0">
    <div className="absolute -inset-6 rounded-3xl blur-3xl opacity-60" style={{ background: THEMES[r.theme].accent, mixBlendMode: "soft-light" }} />
    <img src={`${BACKEND}${r.cover_url}`} alt={`${r.title} cover art`} className="relative w-full aspect-square object-cover rounded-xl shadow-[0_40px_120px_rgba(0,0,0,0.6)] border border-white/10" data-testid="release-cover" />
  </motion.div>
);

function ReleaseInfo({ r, token, onBuy, onRecover, onPreviewPlay }) {
  const theme = THEMES[r.theme];
  return (
    <div className="flex flex-col gap-7">
      <motion.div {...fadeUp(0.15)} className="font-mono text-[10px] tracking-[0.35em] text-white/55">
        / NEW SINGLE {r.explicit && <span className="ml-2 px-1.5 py-0.5 border border-white/40 text-white/80">E</span>}
      </motion.div>
      <motion.div {...fadeUp(0.25)}>
        <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9] uppercase" data-testid="release-title">{r.title}</h1>
        <div className="mt-4"><ArtistLine r={r} /></div>
      </motion.div>
      <motion.div {...fadeUp(0.35)}>
        <PreviewPlayer src={r.preview_url} title={`${r.title} — ${r.artist}`} accent={theme.accent} panel={theme.panel} onPlay={onPreviewPlay} />
      </motion.div>
      <motion.div {...fadeUp(0.45)} className="flex flex-wrap items-center gap-3">
        {token ? (
          <button onClick={() => startDownload(token)} className="release-cta" data-testid="release-download-btn"><Download size={15} /> DOWNLOAD</button>
        ) : (
          <button onClick={onBuy} disabled={!r.available} className="release-cta" data-testid="release-buy-btn">
            <ShoppingBag size={15} /> {r.available ? `BUY $${r.price.toFixed(2)}` : "DOWNLOAD SOON"}
          </button>
        )}
        <div className="text-xs text-white/55 leading-snug">Song + cover art<br />Instant download</div>
      </motion.div>
      <motion.div {...fadeUp(0.55)} className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/10">
        <StreamingLinks links={r.streaming} />
        <button onClick={onRecover} className="text-xs text-white/50 hover:text-white underline-offset-4 hover:underline transition-colors" data-testid="release-recover-link">
          Already bought it?
        </button>
      </motion.div>
    </div>
  );
}

const ShareMeta = ({ r, slug }) => {
  useEffect(() => {
    if (!r) return;
    const links = [...document.querySelectorAll('link[rel="apple-touch-icon"]:not([data-rh])')];
    const prev = links.map((l) => l.href);
    links.forEach((l) => { l.href = `${BACKEND}${r.share_image_url || r.cover_url}`; });
    return () => links.forEach((l, i) => { l.href = prev[i]; });
  }, [r]);
  if (!r) return <Helmet><title>Pulse Media</title></Helmet>;
  const title = `${r.title} — ${r.artist}`;
  const img = `${BACKEND}${r.share_image_url || r.cover_url}`;
  const desc = `Listen to ${r.title} by ${r.artist}. Preview and download for $${r.price.toFixed(2)}.`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={desc} />
      <meta property="og:type" content="music.song" />
      <meta property="og:site_name" content="Pulse Media" />
      <meta property="og:url" content={`${window.location.origin}/${slug}`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:image:secure_url" content={img} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="1200" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:image" content={img} />
      <link rel="apple-touch-icon" href={img} />
      <link rel="image_src" href={img} />
    </Helmet>
  );
};

const TOKEN_TTL_MS = 23 * 3600 * 1000;
const loadToken = (slug) => {
  try {
    const t = JSON.parse(localStorage.getItem(`release_${slug}_dl`) || "null");
    if (t && t.exp > Date.now()) return t.token;
  } catch { /* ignore */ }
  localStorage.removeItem(`release_${slug}_dl`);
  return "";
};

export default function SingleRelease({ slug }) {
  const [r, setR] = useState(null);
  const [failed, setFailed] = useState(false);
  const [modal, setModal] = useState(null);
  const [token, setToken] = useState(() => loadToken(slug));
  const video = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/releases/${slug}`).then((res) => setR(res.data)).catch(() => setFailed(true));
  }, [slug]);
  useEffect(() => {
    if (token) localStorage.setItem(`release_${slug}_dl`, JSON.stringify({ token, exp: Date.now() + TOKEN_TTL_MS }));
  }, [slug, token]);
  usePurchaseReturn(slug, setToken, setModal);

  const theme = THEMES[r?.theme || (slug === "big-mad" ? "red" : "dark")];
  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: theme.bg }} data-testid={`release-page-${slug}`}>
      <ShareMeta r={r} slug={slug} />
      <div className="pointer-events-none absolute inset-0" style={{ background: theme.glow }} />
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
        <Link to="/music" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-white/70 hover:text-white transition-colors" data-testid="release-back-to-music">
          <ArrowLeft size={14} /> ALL MUSIC
        </Link>
        <Link to="/" className="font-mono text-[10px] tracking-[0.35em] text-white/50 hover:text-white transition-colors" data-testid="release-home-link">PULSE MEDIA</Link>
      </header>
      {failed && (
        <div className="relative z-10 text-center py-40 text-white/60 text-sm" data-testid="release-load-error">
          Couldn't load this release. Please refresh in a moment.
        </div>
      )}
      {r && (
        <main className="relative z-10 px-6 lg:px-12 pb-24 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center pt-6 lg:pt-12">
            <Cover r={r} />
            <ReleaseInfo r={r} token={token} onBuy={() => setModal("buy")} onRecover={() => setModal("recover")} onPreviewPlay={() => video.current?.mute()} />
          </div>
          {r.youtube_video_id && (
            <motion.div {...fadeUp(0.6)} className="mt-24">
              <MusicVideo ref={video} videoId={r.youtube_video_id} title={`${r.title} — Music Video`} />
            </motion.div>
          )}
        </main>
      )}
      <AnimatePresence>
        {modal === "buy" && <BuyModal release={r} onClose={() => setModal(null)} />}
        {modal === "recover" && <RecoverModal release={r} onToken={setToken} onClose={() => setModal(null)} />}
        {modal === "success" && r && <SuccessModal release={r} token={token} onClose={() => setModal(null)} />}
      </AnimatePresence>
      <style>{`
        .release-cta { display:inline-flex; align-items:center; gap:10px; padding:18px 34px; border-radius:999px; background:#fff; color:#000;
          font-weight:900; font-size:13px; letter-spacing:0.16em; box-shadow:0 18px 50px rgba(0,0,0,0.35);
          transition: transform .2s ease, box-shadow .2s ease; }
        .release-cta:hover:not(:disabled) { transform: translateY(-2px) scale(1.02); box-shadow:0 24px 60px rgba(0,0,0,0.45); }
        .release-cta:disabled { opacity:.45; cursor:not-allowed; }
      `}</style>
    </div>
  );
}
